import { useState, useEffect, useCallback } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useApp } from '@/contexts/AppContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  CheckSquare, DollarSign, FolderOpen, Share2, Trash2, Search,
  Bell, Sun, Moon, LogOut, Plus, Home, Shield, X, Repeat, Package
} from 'lucide-react';
import { SearchableSelect } from '@/components/SearchableSelect';

const navItems = [
  { path: '/', label: 'Home', icon: Home, exact: true },
  { path: '/tasks', label: 'Tasks', icon: CheckSquare },
  { path: '/expenses', label: 'Expenses', icon: DollarSign },
  { path: '/habits', label: 'Habits', icon: Repeat },
  { path: '/assets', label: 'Assets', icon: Package },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { projects, activeProject, switchProject, notifications, unreadCount, fetchNotifications, theme, toggleTheme } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [showNotif, setShowNotif] = useState(false);
  const [showQuickEntry, setShowQuickEntry] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  // Global Escape key handler - close overlays only
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        // Close any open overlay in the layout
        if (showNotif) { setShowNotif(false); e.stopPropagation(); return; }
        if (showQuickEntry) { setShowQuickEntry(false); e.stopPropagation(); return; }
        if (showSearch) { setShowSearch(false); e.stopPropagation(); return; }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showNotif, showQuickEntry, showSearch]);

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults([]); return; }
    try {
      const { data } = await api.get('/search', { params: { q, project_id: activeProject } });
      setSearchResults(data.results);
    } catch { setSearchResults([]); }
  };

  const markRead = async (id) => {
    await api.put(`/notifications/${id}/read`);
    fetchNotifications();
  };

  const markAllRead = async () => {
    await api.put('/notifications/read-all');
    fetchNotifications();
  };

  const impersonating = !!sessionStorage.getItem('view_as_original_token');
  const impersonatedUsername = sessionStorage.getItem('view_as_target_username');

  const exitViewAs = () => {
    const origToken = sessionStorage.getItem('view_as_original_token');
    if (origToken) localStorage.setItem('token', origToken);
    sessionStorage.removeItem('view_as_token');
    sessionStorage.removeItem('view_as_original_token');
    sessionStorage.removeItem('view_as_target_username');
    window.location.href = '/admin';
  };

  return (
    <div className="min-h-screen bg-background" data-testid="app-layout">
      {impersonating && (
        <div className="bg-amber-500 text-white px-4 py-1.5 flex items-center justify-between text-sm shadow-md" data-testid="view-as-banner">
          <div className="flex items-center gap-2"><Shield className="h-4 w-4" /> Viewing as <span className="font-semibold">@{impersonatedUsername}</span> (impersonation mode)</div>
          <Button size="sm" variant="secondary" className="h-7" onClick={exitViewAs} data-testid="exit-view-as-btn">Exit View As</Button>
        </div>
      )}
      {/* Header */}
      <header className="glass-header fixed top-0 w-full z-50 border-b border-zinc-200 dark:border-zinc-800" data-testid="main-header">
        <div className="flex items-center justify-between h-14 px-4 md:px-6">
          <div className="flex items-center gap-2">
            <NavLink to="/" className="text-lg font-semibold tracking-tight" data-testid="logo-link">Nexus</NavLink>
            {(() => {
              const topLevel = projects.filter(p => !p.parent_project_id);
              const subs = {}; projects.forEach(p => { if (p.parent_project_id) (subs[p.parent_project_id] = subs[p.parent_project_id] || []).push(p); });
              const active = projects.find(p => p.id === activeProject);
              const selectedTop = active?.parent_project_id || activeProject;
              const selectedTopObj = topLevel.find(p => p.id === selectedTop);
              const subOptions = selectedTopObj ? (subs[selectedTopObj.id] || []) : [];
              const currentSubValue = active?.parent_project_id ? activeProject : 'all';
              return (
                <div className="flex items-center gap-1">
                  <Select value={selectedTop} onValueChange={switchProject}>
                    <SelectTrigger className="w-[160px] h-8 border-0 bg-transparent text-sm font-medium" data-testid="workspace-filter">
                      <SelectValue placeholder="All Workspaces" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Workspaces</SelectItem>
                      {topLevel.map(p => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  {selectedTopObj && subOptions.length > 0 && (
                    <Select value={currentSubValue} onValueChange={(v) => switchProject(v === 'all' ? selectedTopObj.id : v)}>
                      <SelectTrigger className="w-[150px] h-8 border-0 bg-transparent text-xs text-zinc-500" data-testid="sub-workspace-filter">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Sub-Workspaces</SelectItem>
                        {subOptions.map(s => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              );
            })()}
          </div>
          <div className="flex items-center gap-1.5">
            <Button variant="ghost" size="icon" onClick={() => setShowSearch(true)} data-testid="search-btn">
              <Search className="h-4 w-4" strokeWidth={1.5} />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setShowNotif(true)} className="relative" data-testid="notifications-btn">
              <Bell className="h-4 w-4" strokeWidth={1.5} />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-rose-500 text-white text-[10px] rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
              {theme === 'light' ? <Moon className="h-4 w-4" strokeWidth={1.5} /> : <Sun className="h-4 w-4" strokeWidth={1.5} />}
            </Button>
            {['admin','super_admin'].includes(user?.role) && (
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin')} className="hidden md:flex" data-testid="admin-link">
                <Shield className="h-4 w-4" strokeWidth={1.5} />
              </Button>
            )}
            <span className="text-xs text-zinc-400 hidden md:block ml-1">{user?.display_name}</span>
            <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn">
              <LogOut className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-14 pb-20 md:pb-6 min-h-[calc(100vh-3.5rem)]">
        <Outlet />
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="md:hidden fixed bottom-0 w-full bg-white dark:bg-zinc-950 border-t border-zinc-200 dark:border-zinc-800 z-40 mobile-nav" data-testid="mobile-bottom-nav">
        <div className="flex justify-around items-center h-14">
          {navItems.map(item => (
            <NavLink key={item.path} to={item.path} end={item.exact}
              className={({ isActive }) => `flex flex-col items-center gap-0.5 text-[10px] font-medium ${isActive ? 'text-zinc-900 dark:text-zinc-100' : 'text-zinc-400'}`}
              data-testid={`mobile-nav-${item.path === '/' ? 'home' : item.path.slice(1)}`}>
              <item.icon className="h-5 w-5" strokeWidth={1.5} />
              {item.label}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Quick Entry FAB */}
      <button
        onClick={() => setShowQuickEntry(true)}
        className="fixed bottom-20 md:bottom-8 right-5 md:right-8 w-14 h-14 rounded-full bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 fab-shadow flex items-center justify-center hover:scale-105 transition-transform z-50"
        data-testid="quick-entry-fab"
      >
        <Plus className="h-6 w-6" strokeWidth={2} />
      </button>

      {/* Quick Entry Dialog */}
      <QuickEntryDialog open={showQuickEntry} onClose={() => setShowQuickEntry(false)} />

      {/* Search Dialog */}
      <Dialog open={showSearch} onOpenChange={setShowSearch}>
        <DialogContent className="max-w-lg" data-testid="search-dialog">
          <DialogHeader>
            <DialogTitle>Search</DialogTitle>
            <DialogDescription>Search across tasks, expenses, and workspaces</DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Type to search..." value={searchQuery}
            onChange={e => handleSearch(e.target.value)}
            className="h-12" autoFocus data-testid="search-input"
          />
          <div className="max-h-80 overflow-y-auto space-y-2">
            {searchResults.map(r => (
              <button key={r.id} className="w-full text-left p-3 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors"
                onClick={() => { setShowSearch(false); navigate(r.type === 'project' ? '/workspaces' : `/${r.type}s`); }}
                data-testid={`search-result-${r.id}`}>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-[10px]">{r.type === 'project' ? 'workspace' : r.type}</Badge>
                  <span className="text-sm font-medium">{r.title || r.name}</span>
                </div>
              </button>
            ))}
            {searchQuery.length >= 2 && searchResults.length === 0 && (
              <p className="text-sm text-zinc-400 text-center py-6">No results found</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Notifications Sheet */}
      <Sheet open={showNotif} onOpenChange={setShowNotif}>
        <SheetContent data-testid="notifications-panel">
          <SheetHeader>
            <SheetTitle className="flex items-center justify-between">
              Notifications
              {unreadCount > 0 && (
                <Button variant="ghost" size="sm" onClick={markAllRead} data-testid="mark-all-read-btn">Mark all read</Button>
              )}
            </SheetTitle>
            <SheetDescription>Your recent notifications</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-1 max-h-[calc(100vh-120px)] overflow-y-auto">
            {notifications.length === 0 && <p className="text-sm text-zinc-400 text-center py-8">No notifications yet</p>}
            {notifications.map(n => (
              <button key={n.id}
                className={`w-full text-left p-4 rounded-lg transition-colors ${!n.read_status ? 'bg-zinc-50 dark:bg-zinc-900' : 'hover:bg-zinc-50 dark:hover:bg-zinc-900'}`}
                onClick={() => markRead(n.id)} data-testid={`notif-${n.id}`}>
                <div className="flex items-start gap-3">
                  {!n.read_status && <span className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 shrink-0" />}
                  <div>
                    <p className="text-sm">{n.message}</p>
                    <p className="text-xs text-zinc-400 mt-1">{new Date(n.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ─── Quick Entry Dialog ───
function QuickEntryDialog({ open, onClose }) {
  const { projects } = useApp();
  const [tab, setTab] = useState('task');
  const [taskForm, setTaskForm] = useState({ title: '', project_id: '', priority: 'medium', description: '' });
  const [expenseForm, setExpenseForm] = useState({ title: '', project_id: '', amount: '', category: 'General', date: new Date().toISOString().slice(0, 10) });
  const [loading, setLoading] = useState(false);

  const focusQfField = (idx) => {
    setTimeout(() => {
      const el = document.querySelector(`[data-qf-idx="${idx}"]`);
      if (el) { el.focus(); if (el.getAttribute('role') === 'combobox') el.click(); }
    }, 60);
  };

  const handleQuickKeyDown = (e, fieldIndex, totalFields, onSave) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (fieldIndex >= totalFields - 1) { onSave(); }
      else { focusQfField(fieldIndex + 1); }
    }
  };

  const saveTask = async () => {
    if (!taskForm.title || !taskForm.project_id) { toast.error('Title and workspace are required'); return; }
    setLoading(true);
    try {
      await api.post('/tasks', taskForm);
      toast.success('Task created');
      setTaskForm({ title: '', project_id: '', priority: 'medium', description: '' });
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    setLoading(false);
  };

  const saveExpense = async () => {
    if (!expenseForm.title || !expenseForm.project_id || !expenseForm.amount) { toast.error('Title, workspace, and amount are required'); return; }
    setLoading(true);
    try {
      await api.post('/expenses', { ...expenseForm, amount: parseFloat(expenseForm.amount) });
      toast.success('Expense created');
      setExpenseForm({ title: '', project_id: '', amount: '', category: 'General', date: new Date().toISOString().slice(0, 10) });
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="quick-entry-dialog">
        <DialogHeader>
          <DialogTitle>Quick Entry</DialogTitle>
          <DialogDescription>Quickly add a new task or expense</DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full">
            <TabsTrigger value="task" className="flex-1" data-testid="quick-tab-task">Task</TabsTrigger>
            <TabsTrigger value="expense" className="flex-1" data-testid="quick-tab-expense">Expense</TabsTrigger>
          </TabsList>
          <TabsContent value="task" className="space-y-3 mt-4">
            <Input placeholder="Task title" value={taskForm.title}
              onChange={e => setTaskForm(p => ({ ...p, title: e.target.value }))}
              data-testid="quick-task-title" data-qf-idx="0" tabIndex={0}
              onKeyDown={e => handleQuickKeyDown(e, 0, 3, saveTask)} />
            <SearchableSelect value={taskForm.project_id} onValueChange={v => setTaskForm(p => ({ ...p, project_id: v }))}
              options={projects.map(p => ({ value: p.id, label: p.name }))} placeholder="Select workspace"
              data-testid="quick-task-project" data-qf-idx="1"
              onMoveToNext={() => focusQfField(2)} />
            <SearchableSelect value={taskForm.priority} onValueChange={v => setTaskForm(p => ({ ...p, priority: v }))}
              options={[{value:'high',label:'High'},{value:'medium',label:'Medium'},{value:'low',label:'Low'}]}
              placeholder="Priority" data-testid="quick-task-priority" data-qf-idx="2"
              onMoveToNext={saveTask} />
            <Button className="w-full" onClick={saveTask} disabled={loading} data-testid="quick-task-save">Save Task</Button>
          </TabsContent>
          <TabsContent value="expense" className="space-y-3 mt-4">
            <Input placeholder="Expense title" value={expenseForm.title}
              onChange={e => setExpenseForm(p => ({ ...p, title: e.target.value }))}
              data-testid="quick-expense-title" data-qf-idx="0" tabIndex={0}
              onKeyDown={e => handleQuickKeyDown(e, 0, 4, saveExpense)} />
            <SearchableSelect value={expenseForm.project_id} onValueChange={v => setExpenseForm(p => ({ ...p, project_id: v }))}
              options={projects.map(p => ({ value: p.id, label: p.name }))} placeholder="Select workspace"
              data-testid="quick-expense-project" data-qf-idx="1"
              onMoveToNext={() => focusQfField(2)} />
            <Input type="number" placeholder="Amount" value={expenseForm.amount}
              onChange={e => setExpenseForm(p => ({ ...p, amount: e.target.value }))}
              data-testid="quick-expense-amount" data-qf-idx="2" tabIndex={0}
              onKeyDown={e => handleQuickKeyDown(e, 2, 4, saveExpense)} />
            <Input type="date" value={expenseForm.date}
              onChange={e => setExpenseForm(p => ({ ...p, date: e.target.value }))}
              data-testid="quick-expense-date" data-qf-idx="3" tabIndex={0}
              onKeyDown={e => handleQuickKeyDown(e, 3, 4, saveExpense)} />
            <Button className="w-full" onClick={saveExpense} disabled={loading} data-testid="quick-expense-save">Save Expense</Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
